import { resolveImagePath } from '@/utils/imageMap.ts';
import type { FooterData } from '@/data/types.ts';

interface FooterProps {
  data: FooterData;
}

export function Footer({ data }: FooterProps) {
  const renderPaymentIcon = (cardType: string) => {
    const imagePath = `/images/payment/${cardType}.png`;
    return (
      <img
        key={cardType}
        src={resolveImagePath(imagePath)}
        alt={`${cardType.charAt(0).toUpperCase() + cardType.slice(1)} Payment Accepted`}
        className="h-6 w-auto"
      />
    );
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          
          {/* Quick Links Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <nav>
              <ul className="space-y-2">
                {data.quickLinks.map((link) => (
                  <li key={link.href}>
                    <a 
                      href={link.href}
                      className="text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Contact Details Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Details</h3>
            
            {/* Address */}
            <div className="mb-3">
              <p className="text-gray-400 font-medium">Address:</p>
              <p className="text-white">
                <a 
                  href={data.contactInfo.address.mapUrl}
                  className="hover:text-yellow-400 transition-colors duration-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {data.contactInfo.address.street} {data.contactInfo.address.city}, {data.contactInfo.address.state} {data.contactInfo.address.zipCode}
                </a>
              </p>
            </div>

            {/* Phone */}
            <div className="mb-3">
              <p className="text-yellow-400 text-lg font-semibold">
                <a 
                  href={`tel:${data.contactInfo.phone}`}
                  className="hover:text-yellow-300 transition-colors duration-200"
                >
                  {data.contactInfo.phone}
                </a>
              </p>
            </div>

            {/* Email */}
            <div className="mb-4">
              <p className="text-gray-400 font-medium">Email:</p>
              <p>
                <a 
                  href={`mailto:${data.contactInfo.email}`}
                  className="text-yellow-400 hover:text-yellow-300 transition-colors duration-200"
                >
                  {data.contactInfo.email}
                </a>
              </p>
            </div>

            {/* Payment Methods */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2 mb-2">
                {data.paymentMethods.cards.map((cardType) => renderPaymentIcon(cardType))}
              </div>
              <p className="text-gray-400 text-sm">{data.paymentMethods.additionalMethods}</p>
            </div>

            {/* Social Media */}
            <div className="flex gap-3">
              <a 
                href={data.contactInfo.socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors duration-200"
                aria-label="Facebook"
              >
                <span className="text-white font-bold text-sm">f</span>
              </a>
              <a 
                href={data.contactInfo.socialMedia.yelp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors duration-200"
                aria-label="Yelp"
              >
                <span className="text-white font-bold text-sm">y</span>
              </a>
            </div>
          </div>

          {/* Business Hours Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Business Hours</h3>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-300">{data.businessHours.weekdays.days}</span>
                <span className="text-white">{data.businessHours.weekdays.hours}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-300">{data.businessHours.weekend.days}</span>
                <span className="text-white">{data.businessHours.weekend.hours}</span>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm mt-4">{data.businessHours.note}</p>
          </div>

        </div>
      </div>

      {/* Map Section */}
      <div className="w-full h-64 bg-gray-800">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2947.2!2d-71.238!3d42.374!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e382d797ee179b%3A0xfb7ceae0b350c2f4!2s17%20Felton%20St%2C%20Waltham%2C%20MA%2002453%2C%20USA!5e0!3m2!1sen!2sus!4v1693920000000!5m2!1sen!2sus"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="All Around Auto Body Location"
        ></iframe>
      </div>

      {/* Copyright Section */}
      <div className="bg-gray-950 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <p className="text-gray-400 text-sm text-center">
            {data.legal.copyright} |{' '}
            <a 
              href={data.legal.termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-yellow-400 hover:text-yellow-300 transition-colors duration-200"
            >
              Terms of Use
            </a>
            {' '}
            <a 
              href={data.legal.privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-yellow-400 hover:text-yellow-300 transition-colors duration-200"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}